import {
  Box,
  Center,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';

type Props = {
  filename: string;
  /** The signed download URL; null while it is being fetched. */
  url: string | null;
  isOpen: boolean;
  onClose: () => void;
};

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp'];

/** Documents carry no content type; the draft names a kept attachment by the type it arrived with. */
const extensionOf = (filename: string): string => filename.split('.').pop()?.toLowerCase() ?? '';
const isImageFile = (filename: string): boolean => IMAGE_EXTENSIONS.includes(extensionOf(filename));
export const isPreviewable = (filename: string): boolean => isImageFile(filename) || extensionOf(filename) === 'pdf';

/** The attachment as the model read it — an image inline, a PDF in a frame — without leaving the draft. */
export const AttachmentPreviewModal = ({ filename, url, isOpen, onClose }: Props) => {
  const isImage = isImageFile(filename);
  const src = url ?? undefined;
  const showLoading = !url;
  const showImage = !!url && isImage;
  const showFrame = !!url && !isImage;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text fontSize="md" noOfLines={1}>
            {filename}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb="6">
          <Show if={showLoading}>
            <Center py="12">
              <Spinner />
            </Center>
          </Show>
          <Show if={showImage}>
            <Image src={src} alt={filename} maxH="75vh" mx="auto" objectFit="contain" />
          </Show>
          <Show if={showFrame}>
            <Box as="iframe" src={src} title={filename} w="full" h="75vh" borderWidth="1px" />
          </Show>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
